import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Share2, Download, Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { generateInsight } from "@/lib/api";
import { getSession, saveSession, themeGradient } from "@/lib/storage";
import { getDeviceId } from "@/lib/device";

const APP_URL = process.env.REACT_APP_APP_URL || "";

function wordCount(s) {
  return (s || "").trim().split(/\s+/).filter(Boolean).length;
}

function pickFontSize(text, format) {
  // Scale font down as word count grows; ensure text fits in card.
  const wc = wordCount(text);
  if (format === "story") {
    if (wc <= 6) return 62;
    if (wc <= 10) return 52;
    return 44;
  }
  // square
  if (wc <= 6) return 44;
  if (wc <= 10) return 36;
  return 30;
}

export default function InsightCard() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const session = useMemo(() => getSession(sessionId), [sessionId]);

  const [loading, setLoading] = useState(!!session && !session.insight);
  const [insight, setInsight] = useState(session?.insight || null);
  const [format, setFormat] = useState("square"); // square | story
  const [error, setError] = useState(null);
  const cardRef = useRef(null);
  const inFlightRef = useRef(false);

  const fetchInsight = async () => {
    if (!session) return;
    // Guard against StrictMode double-invoke + concurrent Try-again clicks.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    // Retry up to 2 times before giving up (transient LLM/network hiccups).
    let lastErr = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const history = (session.messages || [])
          .filter((m) => m.text)
          .map((m) => ({ role: m.role, text: m.text }));
        const res = await generateInsight({
          sessionId,
          mode: session.mode || "vent",
          history,
          deviceId: getDeviceId(),
        });
        const next = { quote: res.quote, theme: res.theme };
        setInsight(next);
        saveSession({ ...session, insight: next });
        setLoading(false);
        inFlightRef.current = false;
        return;
      } catch (err) {
        lastErr = err;
        console.error("insight attempt", attempt, err);
      }
    }
    setError(lastErr || new Error("Insight failed"));
    setLoading(false);
    inFlightRef.current = false;
    toast.error("Couldn't gather a card. Tap Try again.");
  };

  useEffect(() => {
    if (session?.insight || !session) return;
    fetchInsight();
  }, []);

  const theme = insight?.theme || "reflection";
  const [g1, g2] = themeGradient(theme);

  const dims = format === "square" ? { w: 1080, h: 1080 } : { w: 1080, h: 1920 };

  const renderToBlob = async () => {
    if (!insight) return null;
    const canvas = document.createElement("canvas");
    canvas.width = dims.w;
    canvas.height = dims.h;
    const ctx = canvas.getContext("2d");
    // gradient
    const grad = ctx.createLinearGradient(0, 0, dims.w, dims.h);
    grad.addColorStop(0, g1);
    grad.addColorStop(1, g2);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, dims.w, dims.h);
    // vignette
    const rad = ctx.createRadialGradient(dims.w / 2, dims.h / 2, dims.w / 3, dims.w / 2, dims.h / 2, dims.w);
    rad.addColorStop(0, "rgba(0,0,0,0)");
    rad.addColorStop(1, "rgba(0,0,0,0.35)");
    ctx.fillStyle = rad;
    ctx.fillRect(0, 0, dims.w, dims.h);

    // giant quote mark
    ctx.fillStyle = "rgba(244,240,234,0.10)";
    ctx.font = `italic 700 ${dims.w * 0.55}px "Cormorant Garamond", Georgia, serif`;
    ctx.textBaseline = "top";
    ctx.fillText("\u201C", dims.w * 0.02, -dims.w * 0.05);

    // quote
    const fontPx = Math.round((format === "story" ? dims.w * 0.11 : dims.w * 0.085));
    ctx.fillStyle = "#F4F0EA";
    ctx.font = `italic 400 ${fontPx}px "Cormorant Garamond", Georgia, serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    wrapText(ctx, insight.quote, dims.w / 2, dims.h / 2, dims.w * 0.82, fontPx * 1.2);

    // footer
    ctx.font = `500 ${Math.round(dims.w * 0.024)}px "Manrope", system-ui, sans-serif`;
    ctx.fillStyle = "rgba(244,240,234,0.55)";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    const label = APP_URL ? `STILLROOM · ${APP_URL.replace(/^https?:\/\//, "")}` : "STILLROOM";
    ctx.fillText(label, dims.w / 2, dims.h - dims.w * 0.06);

    return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
  };

  const download = async () => {
    const blob = await renderToBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stillroom-${sessionId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Saved to your device.");
  };

  const share = async (label) => {
    const blob = await renderToBlob();
    if (!blob) return;
    const file = new File([blob], "stillroom.png", { type: "image/png" });
    const shareData = {
      title: "Stillroom",
      text: insight?.quote || "",
      files: [file],
    };
    if (navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        /* user cancel */
        return;
      }
    }
    // Fallback: download.
    download();
    toast(`${label} — image saved. You can send it from your gallery.`);
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-[100dvh] items-center justify-center px-8">
        <div className="w-2 h-2 rounded-full bg-still-breathe/70 animate-pulse mb-4" />
        <div className="text-still-muted text-sm text-center leading-relaxed">
          gathering something to leave with you…
        </div>
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="flex flex-col min-h-[100dvh] items-center justify-center px-8 gap-6">
        <p className="font-serif italic text-2xl text-still-muted text-center">
          {error
            ? "Something got in the way."
            : "No card this time."}
        </p>
        {session && (
          <button
            data-testid="retry-insight"
            onClick={fetchInsight}
            className="rounded-full bg-still-fg text-still-bg px-6 py-3"
          >
            Try again
          </button>
        )}
        <button
          data-testid="card-home"
          onClick={() => navigate("/")}
          className="text-still-muted text-sm hover:text-still-fg transition-colors"
        >
          Home
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh] px-6 pt-6 pt-safe pb-10 pb-safe">
      <div className="flex items-center justify-between mb-6">
        <button
          data-testid="card-back"
          onClick={() => navigate("/")}
          className="relative z-10 -m-3 p-3 text-still-muted hover:text-still-fg transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="text-xs uppercase tracking-[0.3em] text-still-muted pointer-events-none">
          A keepsake
        </div>
        <div className="w-11" />
      </div>

      <div className="flex items-center gap-2 justify-center mb-4">
        <button
          data-testid="format-square"
          onClick={() => setFormat("square")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            format === "square"
              ? "bg-still-fg text-still-bg border-still-fg"
              : "bg-transparent text-still-muted border-still-border"
          }`}
        >
          Square
        </button>
        <button
          data-testid="format-story"
          onClick={() => setFormat("story")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            format === "story"
              ? "bg-still-fg text-still-bg border-still-fg"
              : "bg-transparent text-still-muted border-still-border"
          }`}
        >
          Story
        </button>
      </div>

      <motion.div
        ref={cardRef}
        data-testid="insight-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className={`relative mx-auto w-full rounded-3xl overflow-hidden border border-white/10 shadow-[0_8px_40px_rgba(0,0,0,0.4)] ${
          format === "square" ? "aspect-square" : "aspect-[9/16]"
        }`}
        style={{
          maxWidth: format === "square" ? 380 : 300,
          background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)`,
        }}
      >
        {/* giant quote mark */}
        <div
          className="absolute font-serif italic text-still-fg/10 select-none pointer-events-none"
          style={{
            fontSize: format === "square" ? 260 : 320,
            lineHeight: 1,
            top: -30,
            left: -10,
          }}
        >
          &ldquo;
        </div>
        <div className="absolute inset-0 grid place-items-center px-8">
          <p
            className="font-serif italic text-still-fg text-center leading-[1.25]"
            style={{ fontSize: pickFontSize(insight.quote, format) }}
            data-testid="insight-quote"
          >
            {insight.quote}
          </p>
        </div>
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <div className="text-[10px] uppercase tracking-[0.35em] text-still-fg/50">
            {APP_URL ? `Stillroom · ${APP_URL.replace(/^https?:\/\//, "")}` : "Stillroom"}
          </div>
        </div>
      </motion.div>

      <div className="mt-8 grid grid-cols-3 gap-3">
        <button
          data-testid="save-btn"
          onClick={download}
          className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-still-surface border border-still-border py-4 text-still-fg hover:bg-[#26221F] transition-colors"
        >
          <Download size={18} />
          <span className="text-xs">Save</span>
        </button>
        <button
          data-testid="share-btn"
          onClick={() => share("Shared")}
          className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-still-surface border border-still-border py-4 text-still-fg hover:bg-[#26221F] transition-colors"
        >
          <Share2 size={18} />
          <span className="text-xs">Share</span>
        </button>
        <button
          data-testid="send-btn"
          onClick={() => share("Sent")}
          className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-still-surface border border-still-border py-4 text-still-fg hover:bg-[#26221F] transition-colors"
        >
          <Send size={18} />
          <span className="text-xs">Send</span>
        </button>
      </div>

      <p className="text-still-muted text-[11px] mt-6 text-center leading-relaxed">
        Send to someone who needs this.
      </p>
    </div>
  );
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const totalH = lines.length * lineHeight;
  const startY = y - totalH / 2 + lineHeight / 2;
  lines.forEach((ln, i) => ctx.fillText(ln, x, startY + i * lineHeight));
}
